import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, FileText, ShieldAlert } from "lucide-react";
import { Card } from "@samklang/ui";
import { useKartlaggningStore } from "@/stores/kartlaggning-store";
import { buildSamladBild, buildUskPerspectiveSections, neutrallyDescribeDifference } from "./lib";

interface Props {
  readonly kartlaggningId: string;
}

export function KartlaggningUskScreen({ kartlaggningId }: Props) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const sections = useMemo(() => (kartlaggning ? buildUskPerspectiveSections(kartlaggning) : []), [kartlaggning]);
  const samladBild = useMemo(() => (kartlaggning ? buildSamladBild(kartlaggning) : undefined), [kartlaggning]);

  if (!kartlaggning) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card>
          <h1 className="font-display text-2xl font-semibold">Kartläggningen finns inte.</h1>
          <button onClick={() => void navigate({ to: "/kartlaggningar" })} className="mt-4 font-semibold text-primary underline">Till listan</button>
        </Card>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-4xl px-5 py-8 sm:px-8">
      <button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })} className="flex items-center gap-1 text-sm font-semibold text-primary">
        <ArrowLeft size={16} /> Till kartläggningen
      </button>
      <div className="mt-3 flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary"><FileText size={24} /></span>
        <div>
          <h1 className="font-display text-3xl font-semibold">USK-underlag</h1>
          <p className="text-foreground-muted">Utredning om särskilt stöd · {kartlaggning.participantReference || "lokal id saknas"}</p>
        </div>
      </div>

      <Card className="mt-6 bg-warning/10">
        <p className="flex items-start gap-2 text-sm leading-relaxed">
          <ShieldAlert size={18} className="mt-0.5 shrink-0" />
          <span>
            <strong>Detta underlag stödjer professionell bedömning och avgör inte särskilt stöd.</strong> Det bygger på de perspektiv som samlats in och ska läsas tillsammans med den professionella bedömningen.
          </span>
        </p>
      </Card>

      {sections.length === 0 ? (
        <Card className="mt-6"><p className="text-foreground-muted">Inga perspektiv är genomförda ännu — USK-underlaget fylls på när svar finns.</p></Card>
      ) : null}

      <div className="mt-6 grid gap-4">
        {sections.map((section) => (
          <Card key={section.heading} padding="lg">
            <h2 className="font-display text-2xl font-semibold">{section.heading}</h2>
            <p className="mt-1 text-xs text-foreground-subtle">{section.source}</p>
            <div className="mt-4 space-y-4">
              {section.blocks.length === 0 ? (
                <p className="text-sm text-foreground-subtle">Fylls i vid professionell bedömning.</p>
              ) : (
                section.blocks.map((block) => (
                  <div key={block.heading} className="rounded-2xl bg-surface-muted p-4">
                    <h3 className="font-semibold">{block.heading}</h3>
                    {block.items.length === 0 ? (
                      <p className="mt-1 text-sm text-foreground-subtle">Fylls i vid professionell bedömning.</p>
                    ) : (
                      <ul className="mt-2 space-y-1.5">
                        {block.items.map((item, index) => (
                          <li key={index} className="text-sm leading-relaxed">{item.question}: {item.answer}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>
        ))}

        {samladBild ? (
          <Card padding="lg">
            <h2 className="font-display text-2xl font-semibold">Samlad bild</h2>
            <div className="mt-4 grid gap-4">
              <Block heading="Gemensamma nämnare">
                {samladBild.agreementRows.length === 0 ? <Muted /> : samladBild.agreementRows.map((row) => <p key={row.questionId}><strong>{row.question}</strong> — {row.cells[0]?.answer}</p>)}
              </Block>
              <Block heading="Skillnader">
                {samladBild.differenceRows.length === 0 ? <Muted /> : samladBild.differenceRows.map((row) => <p key={row.questionId} className="leading-relaxed"><strong>{row.question}</strong> — {neutrallyDescribeDifference(row)}</p>)}
              </Block>
              <Block heading="Styrkor och fungerande situationer">
                {samladBild.strengths.length === 0 ? <Muted /> : samladBild.strengths.map((e, i) => <p key={i} className="leading-relaxed">[{e.label}] {e.item.question}: {e.item.answer}</p>)}
              </Block>
              <Block heading="Svårigheter">
                {samladBild.difficulties.length === 0 ? <Muted /> : samladBild.difficulties.map((e, i) => <p key={i} className="leading-relaxed">[{e.label}] {e.item.question}: {e.item.answer}</p>)}
              </Block>
              <Block heading="Behov">
                {samladBild.needs.length === 0 ? <Muted /> : samladBild.needs.map((e, i) => <p key={i} className="leading-relaxed">[{e.label}] {e.item.question}: {e.item.answer}</p>)}
              </Block>
              <Block heading="Lärmiljöfaktorer, anpassningar + effekt och utforska vidare">
                <Muted text="Fylls i vid professionell bedömning, till exempel utifrån frågorna om professionellt stöd under dialogen." />
              </Block>
            </div>
          </Card>
        ) : null}
      </div>
    </section>
  );
}

function Block({ heading, children }: { readonly heading: string; readonly children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-surface-muted p-4">
      <h3 className="font-semibold">{heading}</h3>
      <div className="mt-2 space-y-1.5 text-sm">{children}</div>
    </div>
  );
}

function Muted({ text = "Inga uppgifter ännu." }: { readonly text?: string }) {
  return <p className="text-foreground-subtle">{text}</p>;
}
